import React, { useCallback } from 'react';
import { TextField, Button, Menu, MenuItem, MenuProps, InputAdornment } from '@material-ui/core';
import Autocomplete, { AutocompleteProps, AutocompleteRenderInputParams } from '@material-ui/lab/Autocomplete';
import { ExpandMore } from '@material-ui/icons';
import styles from './styles.module.scss';

const AloDropdown = ({
  searchable = false,
  dropdownItems,
  defaultValue,
  MUIMenuProps,
  MUIAutocompleteProps,
  onChange,
  placeholder,
  value,
  wrapperClass = '',
  searchableDropdownPrependIcon,
  inputReadOnly = false,
  popoverClass,
  disableTranslation,
  labelPrefix,
  hasBorder = true,
}: AloDropdownProps) => {
  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
  const [selectedValue, setSelectedValue] = React.useState<DropdownItem | undefined>(defaultValue);
  const [isDropdownOpen, setIsDropdownOpen] = React.useState<boolean>(false);

  const collapseDropdown = () => {
    setAnchorEl(null);
  };

  const onMenuClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const onMenuItemClick = (value: DropdownItem) => {
    collapseDropdown();
    setSelectedValue(value);
    onChange(value);
  };

  const handleTextClick = useCallback(() => {
    setIsDropdownOpen(!isDropdownOpen);
  }, [isDropdownOpen]);

  const popupIcon = (
    <ExpandMore className={`${styles.expandMoreIcon} ${labelPrefix ? styles.expandMoreIconColor : ''}`} />
  );

  const renderInput = (params: AutocompleteRenderInputParams) => {
    const startAdornmentItems = [];

    if (searchableDropdownPrependIcon) {
      startAdornmentItems.push(
        <InputAdornment position="start">
          <span className="GlobalLinkNoTx">{searchableDropdownPrependIcon}</span>
        </InputAdornment>
      );
    }

    if (labelPrefix) {
      startAdornmentItems.push(
        <InputAdornment position="start">
          <span
            className={`${styles.labelPrefix} ${!value?.text ? styles.labelPrefixBold : ''}`}
            onClick={handleTextClick}
          >
            {value?.text ? `${labelPrefix}: ` : labelPrefix}
          </span>
        </InputAdornment>
      );
    }

    const inputProps = {
      ...params.InputProps,
      startAdornment: startAdornmentItems.length > 0 ? startAdornmentItems : undefined,
      classes: {
        notchedOutline: styles.noBorder,
        input: `${inputReadOnly ? styles.inputReadOnly : ''} ${!value?.text ? styles.hideInput : ''}`,
      },
      type: inputReadOnly ? 'button' : 'text',
      readOnly: inputReadOnly,
    };

    return (
      <TextField
        {...params}
        InputProps={inputProps}
        className={`${styles.textField} ${!hasBorder ? styles.noBorder : ''} ${disableTranslation && 'GlobalLinkNoTx'}`}
        variant="outlined"
      />
    );
  };

  return (
    <div className={`${styles.aloDropdown} alo-dropdown ${wrapperClass}`}>
      {searchable ? (
        <Autocomplete
          disableClearable={inputReadOnly ? true : false}
          classes={{
            root: styles.autocompleteRoot,
            inputRoot: styles.inputRoot,
            option: styles.option,
            endAdornment: styles.endAdornment,
            popper: styles.autocompletePopper,
          }}
          defaultValue={defaultValue ? selectedValue : undefined}
          options={dropdownItems}
          onChange={(event, value: DropdownItem | null) => value && onChange(value)}
          popupIcon={popupIcon}
          renderInput={renderInput}
          getOptionLabel={({ text }) => text}
          value={value}
          open={isDropdownOpen}
          onOpen={() => setIsDropdownOpen(true)}
          onClose={() => setIsDropdownOpen(false)}
          {...MUIAutocompleteProps}
        />
      ) : (
        <>
          <Button onClick={onMenuClick} className={styles.menuButton} aria-hidden={Boolean(anchorEl)}>
            {selectedValue?.text || placeholder}
            {popupIcon}
          </Button>
          <Menu
            anchorEl={anchorEl}
            keepMounted
            open={Boolean(anchorEl)}
            onClose={collapseDropdown}
            className={`${styles.popoverClass} ${popoverClass}`}
            {...MUIMenuProps}
          >
            {dropdownItems?.map((dropdownItem: DropdownItem) => (
              <MenuItem
                className={styles.menuItem}
                onClick={() => onMenuItemClick(dropdownItem)}
                selected={dropdownItem.key === selectedValue?.key}
              >
                {dropdownItem.text}
              </MenuItem>
            ))}
          </Menu>
        </>
      )}
    </div>
  );
};

export default AloDropdown;
export interface AloDropdownProps {
  searchable?: boolean;
  searchableDropdownPrependIcon?: JSX.Element | null;
  dropdownItems: DropdownItem[];
  defaultValue?: DropdownItem;
  MUIMenuProps?: Omit<MenuProps, 'open' | 'children'>;
  MUIAutocompleteProps?: MUIAutocompletePropsType;
  onChange: (value: DropdownItem) => void;
  placeholder: string;
  value?: DropdownItem;
  wrapperClass?: string;
  inputReadOnly?: boolean;
  popoverClass?: string;
  disableTranslation?: boolean;
  labelPrefix?: string;
  hasBorder?: boolean;
}

export type MUIAutocompletePropsType = Omit<
  AutocompleteProps<DropdownItem, undefined, boolean, undefined>,
  | 'options'
  | 'value'
  | 'defaultValue'
  | 'onChange'
  | 'popupIcon'
  | 'renderInput'
  | 'getOptionLabel'
  | 'disableTranslation'
>;

export interface DropdownItem {
  key: string;
  text: string;
  flag?: string;
}
